import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkle, CheckCircle, Warning, Quotes as QuotesIcon, ArrowRight,
  X as XIcon, MagnifyingGlass,
} from "@phosphor-icons/react";
import Logo from "../components/Logo";
import client from "../lib/api";

/*
  TestimonialRequestPage — public form where a customer submits their
  testimonial after clicking the "share your quote" link in a founder ask.
  Public route: /testimonial/:token
*/

export default function TestimonialRequestPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, ready: false, prefill: {} });
  const [form, setForm] = useState({ quote: "", person: "", role: "", is_public_ok: true });
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoStoragePath, setPhotoStoragePath] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [cropSourceUrl, setCropSourceUrl] = useState(null);  // objectURL of picked-but-uncropped file
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Share your FreightPilot AI experience";
    (async () => {
      try {
        // Forward any UTM params from the URL — powers the truck-show
        // dashboard's scan-attribution when a vinyl QR is scanned.
        const params = new URLSearchParams(window.location.search);
        const utmKeys = ["utm_source", "utm_medium", "utm_campaign"];
        const utmQs = utmKeys
          .map((k) => (params.get(k) ? `${k}=${encodeURIComponent(params.get(k))}` : ""))
          .filter(Boolean)
          .join("&");
        const url = utmQs
          ? `/testimonial-request/${token}?${utmQs}`
          : `/testimonial-request/${token}`;
        const { data } = await client.get(url);
        if (data.status === "ready") {
          setState({ loading: false, ready: true, prefill: data.prefill || {} });
          setForm((f) => ({
            ...f,
            person: (data.prefill && data.prefill.person) || "",
          }));
        } else if (data.status === "already_submitted") {
          setState({ loading: false, ready: false, error: "Already submitted — thank you!" });
        } else if (data.status === "expired") {
          setState({ loading: false, ready: false, error: "This link expired. Ask Mike for a fresh one." });
        } else {
          setState({ loading: false, ready: false, error: "This link isn't valid." });
        }
      } catch (e) {
        setState({
          loading: false,
          ready: false,
          error: e?.response?.data?.detail || "This testimonial link isn't valid or expired.",
        });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.quote.trim().length < 20) {
      toast.error("Quote needs to be at least 20 characters — you've got this!");
      return;
    }
    if (!form.person.trim() || !form.role.trim()) {
      toast.error("Please fill in your name + role");
      return;
    }
    setSubmitting(true);
    try {
      await client.post(`/testimonial-request/${token}/submit`, {
        ...form,
        photo_storage_path: photoStoragePath || undefined,
      });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // File input handler — instead of uploading directly, opens the crop
  // modal so pilots don't have to worry about aspect ratio before hitting
  // "upload". The modal calls `handleCropComplete` with a Blob when they
  // hit Save.
  const openCropper = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo needs to be under 5 MB — please pick a smaller image");
      return;
    }
    if (!/^image\//.test(file.type)) {
      toast.error("Please pick a JPG, PNG, WebP or GIF image");
      return;
    }
    // Revoke any previous object URL to prevent memory leak.
    if (cropSourceUrl) URL.revokeObjectURL(cropSourceUrl);
    setCropSourceUrl(URL.createObjectURL(file));
  };

  const handleCropComplete = async (blob) => {
    if (cropSourceUrl) {
      URL.revokeObjectURL(cropSourceUrl);
      setCropSourceUrl(null);
    }
    if (!blob) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      // Give it a stable filename so the backend picks the right extension.
      fd.append("file", blob, "pilot-quote-photo.jpg");
      const { data } = await client.post(
        `/testimonial-request/${token}/upload-photo`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPhotoStoragePath(data.storage_path);
      setPhotoUrl(data.photo_url + `?t=${Date.now()}`);
      toast.success("Photo uploaded — looks good!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg" data-testid="testimonial-request-page">
      {cropSourceUrl && (
        <PhotoCropModal
          src={cropSourceUrl}
          onCancel={() => {
            URL.revokeObjectURL(cropSourceUrl);
            setCropSourceUrl(null);
          }}
          onSave={handleCropComplete}
        />
      )}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/"><Logo variant="sm" /></Link>
          <Link to="/" className="text-xs text-neutral-400 hover:text-white">← Back to freightpilotai.ai</Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
        {state.loading ? (
          <div className="text-neutral-500 text-sm">Loading…</div>
        ) : done ? (
          <SuccessPanel />
        ) : !state.ready ? (
          <ErrorPanel message={state.error || "This link isn't valid."} />
        ) : (
          <>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5A1F]/25 bg-[#FF5A1F]/5 text-[11px] uppercase tracking-widest text-[#FF9764] mb-6" data-testid="request-hero-pill">
              <Sparkle size={12} weight="fill" /> From Mike
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold leading-tight tracking-tight mb-3">
              Share your FreightPilot AI moment.
            </h1>
            <p className="text-neutral-400 text-base leading-relaxed max-w-xl mb-8">
              Two or three sentences is plenty. Speak plainly — like you would to a
              fellow trucker in a Waffle House parking lot. Your quote may appear on
              our <Link to="/customers" className="text-[#FF9764] hover:text-[#FFB08A] underline underline-offset-2">customer stories page</Link>.
            </p>

            <form onSubmit={submit} className="card-surface p-6 sm:p-8 space-y-5" data-testid="testimonial-form">
              <Field
                label="What's changed since you started using FreightPilot AI?"
                required
                hint="20–800 characters. Trucker-to-trucker plain speech beats marketing polish every time."
              >
                <div className="relative">
                  <QuotesIcon size={22} weight="fill" className="absolute top-3 left-3 text-[#FF5A1F]/30" />
                  <textarea
                    required
                    minLength={20}
                    maxLength={800}
                    rows={5}
                    value={form.quote}
                    onChange={(e) => setForm({ ...form, quote: e.target.value })}
                    placeholder="What's the ONE thing that's actually different about running your dispatch now?"
                    data-testid="input-quote"
                    className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-3 pl-11 pr-3 focus:outline-none focus:border-[#FF5A1F]/60 resize-y"
                  />
                </div>
                <div className="text-[11px] text-neutral-500 mt-1">{form.quote.length}/800</div>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Your name" required hint="How you want to be credited">
                  <input
                    required
                    maxLength={80}
                    value={form.person}
                    onChange={(e) => setForm({ ...form, person: e.target.value })}
                    placeholder="Marcus R."
                    data-testid="input-person"
                    className={inputCls}
                  />
                </Field>
                <Field label="Your role + fleet" required hint="E.g. Owner · Flatbed, 22 trucks · TX">
                  <input
                    required
                    maxLength={120}
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    placeholder="Owner · Flatbed, 22 trucks · TX"
                    data-testid="input-role"
                    className={inputCls}
                  />
                </Field>
              </div>

              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.is_public_ok}
                  onChange={(e) => setForm({ ...form, is_public_ok: e.target.checked })}
                  data-testid="input-consent"
                  className="mt-1 accent-[#FF5A1F]"
                />
                <div>
                  <div className="text-sm text-white font-medium">Show my full name publicly</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">
                    If unchecked, we'll credit you as "First-name L." (initial only) on the customer stories page.
                  </div>
                </div>
              </label>

              {/* Photo upload — optional but makes the quote feel real */}
              <div data-testid="photo-upload-field">
                <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">
                  Add a photo (optional)
                </label>
                <div className="flex items-center gap-4">
                  {photoUrl ? (
                    <div className="relative shrink-0">
                      <img
                        src={photoUrl}
                        alt="Uploaded"
                        data-testid="photo-preview"
                        className="w-20 h-20 rounded-full object-cover border-2 border-[#FF5A1F]/40"
                      />
                      <button
                        type="button"
                        onClick={() => { setPhotoStoragePath(null); setPhotoUrl(null); }}
                        data-testid="photo-remove-btn"
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black border border-white/20 text-neutral-400 hover:text-white text-xs flex items-center justify-center"
                        title="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-full border-2 border-dashed border-white/15 flex items-center justify-center shrink-0 text-neutral-600">
                      <span className="text-[10px] uppercase tracking-widest">No photo</span>
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold text-white cursor-pointer hover:border-[#FF5A1F]/40 hover:bg-[#FF5A1F]/10 transition-colors">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={(e) => {
                          openCropper(e.target.files?.[0]);
                          // Reset so picking the same file twice still fires.
                          e.target.value = "";
                        }}
                        data-testid="input-photo"
                        disabled={uploadingPhoto}
                        className="hidden"
                      />
                      {uploadingPhoto ? "Uploading…" : (photoUrl ? "Replace photo" : "Upload photo")}
                    </label>
                    <div className="text-[11px] text-neutral-500 mt-1.5 leading-snug">
                      JPG / PNG / WebP · max 5 MB. You'll get a quick crop step — no aspect-ratio worries.
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-testimonial-btn"
                className="w-full rounded-full py-3 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors"
              >
                {submitting ? "Submitting…" : (
                  <>Share my quote <ArrowRight size={14} weight="bold" /></>
                )}
              </button>

              <p className="text-[11px] text-neutral-500 text-center leading-relaxed">
                Reviewed within a few hours — we're on 24/7. We'll never publish critical feedback publicly —
                just use it to fix things. Reply to Mike's email if you want to change anything.
              </p>
            </form>
          </>
        )}
      </main>
    </div>
  );
}

/* ─── SUCCESS ─── */
function SuccessPanel() {
  return (
    <div className="card-surface p-10 sm:p-14 text-center" data-testid="testimonial-success">
      <CheckCircle size={48} weight="fill" className="text-emerald-400 mx-auto mb-5" />
      <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-2">Thank you.</h2>
      <p className="text-neutral-400 text-base leading-relaxed max-w-md mx-auto mb-6">
        Mike will review your quote personally within a few hours — we're available 24/7. If it goes
        on the customer stories page, you'll see it live at freightpilotai.ai/customers.
      </p>
      <Link
        to="/customers"
        className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold border border-white/15 hover:bg-white/5 transition-colors"
      >
        See other stories <ArrowRight size={14} weight="bold" />
      </Link>
    </div>
  );
}

/* ─── ERROR ─── */
function ErrorPanel({ message }) {
  return (
    <div className="card-surface p-10 sm:p-14 text-center" data-testid="testimonial-error">
      <Warning size={44} weight="fill" className="text-amber-400 mx-auto mb-5" />
      <h2 className="font-display text-xl sm:text-2xl font-semibold mb-2">Link problem</h2>
      <p className="text-neutral-400 text-sm leading-relaxed max-w-md mx-auto mb-6">{message}</p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038]"
      >
        Back to FreightPilot AI <ArrowRight size={14} weight="bold" />
      </Link>
    </div>
  );
}

/* ─── Small helpers ─── */
const inputCls =
  "w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2 px-3 focus:outline-none focus:border-[#FF5A1F]/60";

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {hint && <div className="text-[11px] text-neutral-500 mt-1 leading-snug">{hint}</div>}
    </div>
  );
}


/* ─── PhotoCropModal ───
   Simple circular-crop UI. User picks an image, we render it inside a
   canvas overlay where they can zoom (0.5×–3×) and drag to position.
   On Save, we render the crop to an off-screen canvas → JPEG blob (512×512).
   Zero external deps — HTML5 canvas only. */
function PhotoCropModal({ src, onCancel, onSave }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imgReady, setImgReady] = useState(false);
  const [saving, setSaving] = useState(false);

  const CANVAS_SIZE = 320;  // preview + crop viewport (square, rendered as circle via CSS)
  const OUTPUT_SIZE = 512;  // final JPEG size in pixels

  // Load the image once. We use createImageBitmap with imageOrientation:
  // 'from-image' so portrait phone shots (which land as landscape + EXIF
  // orientation=6/8) get auto-rotated to their intended orientation
  // BEFORE the pilot sees the crop viewport. No sideways selfies.
  useEffect(() => {
    let cancelled = false;
    let bitmap = null;
    (async () => {
      try {
        // Fetch the object URL as a blob so createImageBitmap accepts it.
        const resp = await fetch(src);
        const blob = await resp.blob();
        try {
          bitmap = await window.createImageBitmap(blob, {
            imageOrientation: "from-image",
            premultiplyAlpha: "premultiply",
          });
        } catch (e) {
          // Older browser without imageOrientation support — fall back
          // to a plain <img>. Rotation won't happen but crop still works.
          const img = new window.Image();
          img.crossOrigin = "anonymous";
          await new Promise((res, rej) => {
            img.onload = res;
            img.onerror = rej;
            img.src = src;
          });
          bitmap = img;
        }
        if (cancelled) return;
        imgRef.current = bitmap;
        const w = bitmap.width;
        const h = bitmap.height;
        setImgReady(true);
        setZoom(Math.max(CANVAS_SIZE / w, CANVAS_SIZE / h));
        setOffset({ x: 0, y: 0 });
      } catch (e) {
        if (!cancelled) toast.error("Couldn't load that image — try another");
      }
    })();
    return () => {
      cancelled = true;
      if (bitmap && bitmap.close) {
        try { bitmap.close(); } catch (_) { /* noop */ }
      }
    };
  }, [src]);

  // Redraw the preview whenever inputs change.
  useEffect(() => {
    if (!imgReady || !canvasRef.current || !imgRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.fillStyle = "#0A0A0A";
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    const img = imgRef.current;
    const drawW = img.width * zoom;
    const drawH = img.height * zoom;
    const drawX = (CANVAS_SIZE - drawW) / 2 + offset.x;
    const drawY = (CANVAS_SIZE - drawH) / 2 + offset.y;
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
  }, [imgReady, zoom, offset]);

  const onMouseDown = (e) => {
    setDragging(true);
    const r = canvasRef.current.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    setDragStart({ x: cx - offset.x, y: cy - offset.y });
  };
  const onMouseMove = (e) => {
    if (!dragging) return;
    const r = canvasRef.current.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    setOffset({ x: cx - dragStart.x, y: cy - dragStart.y });
  };
  const onMouseUp = () => setDragging(false);

  const save = async () => {
    if (!imgRef.current) return;
    setSaving(true);
    try {
      // Render the crop to an off-screen canvas at OUTPUT_SIZE.
      const out = document.createElement("canvas");
      out.width = OUTPUT_SIZE;
      out.height = OUTPUT_SIZE;
      const octx = out.getContext("2d");
      const img = imgRef.current;
      const scale = OUTPUT_SIZE / CANVAS_SIZE;
      const drawW = img.width * zoom * scale;
      const drawH = img.height * zoom * scale;
      const drawX = ((CANVAS_SIZE - img.width * zoom) / 2 + offset.x) * scale;
      const drawY = ((CANVAS_SIZE - img.height * zoom) / 2 + offset.y) * scale;
      octx.fillStyle = "#0A0A0A";
      octx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      octx.drawImage(img, drawX, drawY, drawW, drawH);
      // Export as JPEG at 90% quality — keeps size small even for tall
      // portrait uploads while preserving detail.
      const blob = await new Promise((res) => out.toBlob(res, "image/jpeg", 0.9));
      await onSave(blob);
    } catch (e) {
      toast.error("Crop failed — try again");
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="photo-crop-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    >
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0A0A0A] shadow-[0_0_80px_-20px_rgba(255,90,31,0.35)] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold">Crop your photo</div>
            <div className="text-sm text-white mt-0.5">Drag to reposition · scroll or slide to zoom</div>
          </div>
          <button
            onClick={onCancel}
            data-testid="photo-crop-cancel"
            className="w-8 h-8 rounded-full border border-white/10 text-neutral-400 hover:text-white hover:border-white/30 flex items-center justify-center"
          >
            <XIcon size={13} weight="bold" />
          </button>
        </div>

        <div className="p-6 flex flex-col items-center">
          <div
            className="relative"
            style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
          >
            <canvas
              ref={canvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              onTouchStart={onMouseDown}
              onTouchMove={onMouseMove}
              onTouchEnd={onMouseUp}
              data-testid="photo-crop-canvas"
              className="rounded-full cursor-move bg-neutral-900 border-2 border-[#FF5A1F]/40"
              style={{ width: CANVAS_SIZE, height: CANVAS_SIZE, touchAction: "none" }}
            />
            {/* Dashed outer ring so the crop boundary is unambiguous */}
            <div
              className="absolute inset-0 rounded-full pointer-events-none border border-dashed border-white/20"
              style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)" }}
            />
          </div>

          <div className="mt-5 w-full flex items-center gap-3">
            <MagnifyingGlass size={14} className="text-neutral-400" />
            <input
              type="range"
              min={0.2}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              data-testid="photo-crop-zoom"
              className="flex-1 accent-[#FF5A1F]"
            />
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 tabular-nums w-8 text-right">
              {zoom.toFixed(1)}×
            </span>
          </div>

          <div className="mt-6 w-full flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              data-testid="photo-crop-cancel-btn"
              className="flex-1 rounded-full py-2.5 text-sm font-semibold border border-white/15 hover:bg-white/5 text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !imgReady}
              data-testid="photo-crop-save-btn"
              className="flex-[2] rounded-full py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-40"
            >
              {saving ? "Saving…" : "Save & upload"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
